import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_c42ba4e9-9f23-42d1-8dca-60d6e029ce6f/artifacts/b4bmdmcp_IMG_7020.jpeg";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "Services", path: "/services" },
  { name: "Gallery", path: "/gallery" },
  { name: "About", path: "/about" },
  { name: "Contact", path: "/contact" },
];

export default function Navbar() {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <nav
      data-testid="navbar"
      className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b border-neutral-200"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
          <img
            src={LOGO_URL}
            alt="Mid America Star Builders LLC"
            className="h-14 w-auto"
          />
          <div className="hidden sm:block">
            <span className="font-outfit font-black text-lg tracking-tight text-brand-dark leading-tight block">
              MID AMERICA
            </span>
            <span className="font-outfit text-xs tracking-[0.15em] uppercase text-brand font-bold block">
              Star Builders LLC
            </span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-link-${link.name.toLowerCase()}`}
              className={`font-outfit text-sm font-semibold uppercase tracking-wider transition-colors hover:text-brand ${
                location.pathname === link.path
                  ? "text-brand"
                  : "text-brand-dark"
              }`}
            >
              {link.name}
            </Link>
          ))}
        </div>

        {/* Desktop CTA */}
        <div className="hidden lg:flex items-center gap-4">
          <a
            href="tel:9014061803"
            className="flex items-center gap-2 font-dm-sans text-sm font-semibold text-brand-dark"
            data-testid="navbar-phone"
          >
            <Phone className="w-4 h-4 text-brand" />
            (901) 406-1803
          </a>
          <Link to="/contact">
            <Button
              data-testid="navbar-cta"
              className="bg-brand text-white font-outfit font-bold uppercase tracking-wider text-sm px-6 py-2 hover:bg-brand-hover rounded-none"
            >
              Free Quote
            </Button>
          </Link>
        </div>

        {/* Mobile Menu */}
        <div className="lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-trigger"
                className="p-2 text-brand-dark"
              >
                <Menu className="w-6 h-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 bg-white p-0">
              <div className="flex flex-col h-full">
                <div className="p-6 border-b border-neutral-200">
                  <img src={LOGO_URL} alt="MASB" className="h-12 w-auto" />
                </div>
                <div className="flex flex-col p-6 gap-1">
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setOpen(false)}
                      className={`font-outfit text-base font-semibold uppercase tracking-wider py-3 px-4 transition-colors hover:bg-neutral-100 ${
                        location.pathname === link.path
                          ? "text-brand bg-neutral-50"
                          : "text-brand-dark"
                      }`}
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
                <div className="mt-auto p-6 border-t border-neutral-200 space-y-4">
                  <a
                    href="tel:9014061803"
                    className="flex items-center gap-2 font-dm-sans font-semibold text-brand-dark"
                  >
                    <Phone className="w-4 h-4 text-brand" />
                    (901) 406-1803
                  </a>
                  <Link to="/contact" onClick={() => setOpen(false)}>
                    <Button className="w-full bg-brand text-white font-outfit font-bold uppercase tracking-wider hover:bg-brand-hover rounded-none">
                      Free Quote
                    </Button>
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
}
